import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { sendGetClientRequest } from "../../../api/helper";
import { serialize } from "../../../utils";

const initialState = {
  rows: [],
  total: 0,
  page: 1,
  limit: 10,
  loading: false,
  error: null,
  filter: {
    search: "",
    status: "",
    sort_dir: "desc",
  },
  detail: null,
  detailLoading: false,
};

const buildQuery = ({ filter = {}, page, limit }) => {
  const q = {};
  if (filter.search) q.search = filter.search;
  if (filter.status) q.status = filter.status;
  if (filter.sort_dir) q.sort_dir = filter.sort_dir;
  if (page) q.page = page;
  if (limit) q.limit = limit;
  return Object.keys(q).length ? `?${serialize(q)}` : "";
};

const unwrapOrThrow = (response) => {
  if (!response || response.error) {
    const err = new Error(response?.msg || "API error");
    err.payload = response?.data;
    throw err;
  }
  return response.data;
};

// GET /api/personal-subscriptions → data: { rows, total, limit, page }
export const fetchPersonalSubscriptions = createAsyncThunk(
  "personalSubscription/fetchList",
  async ({ filter, page, limit } = {}) => {
    const qs = buildQuery({ filter, page, limit });
    const response = await sendGetClientRequest(
      `/api/personal-subscriptions${qs}`
    );
    return unwrapOrThrow(response) ?? { rows: [], total: 0 };
  }
);

// GET /api/personal-subscriptions/{id}
export const fetchPersonalSubscriptionDetail = createAsyncThunk(
  "personalSubscription/fetchDetail",
  async (id) => {
    const response = await sendGetClientRequest(
      `/api/personal-subscriptions/${id}`
    );
    return unwrapOrThrow(response);
  }
);

const personalSubscriptionSlice = createSlice({
  name: "personalSubscription",
  initialState,
  reducers: {
    setFilter: (state, { payload }) => {
      state.filter = { ...state.filter, ...payload };
      state.page = 1;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    resetDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: {
    [fetchPersonalSubscriptions.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [fetchPersonalSubscriptions.fulfilled]: (state, { payload }) => {
      state.loading = false;
      state.rows = Array.isArray(payload?.rows) ? payload.rows : [];
      state.total = Number.isFinite(payload?.total) ? payload.total : 0;
      if (Number.isInteger(payload?.page) && payload.page > 0) {
        state.page = payload.page;
      }
      if (Number.isInteger(payload?.limit) && payload.limit > 0) {
        state.limit = payload.limit;
      }
    },
    [fetchPersonalSubscriptions.rejected]: (state, { error }) => {
      state.loading = false;
      state.error = error?.message ?? "fetch failed";
    },

    [fetchPersonalSubscriptionDetail.pending]: (state) => {
      state.detailLoading = true;
    },
    [fetchPersonalSubscriptionDetail.fulfilled]: (state, { payload }) => {
      state.detailLoading = false;
      state.detail = payload;
    },
    [fetchPersonalSubscriptionDetail.rejected]: (state) => {
      state.detailLoading = false;
    },
  },
});

export const { setFilter, setPage, resetDetail } =
  personalSubscriptionSlice.actions;
export const selectPersonalSubscription = (state) => state.personalSubscription;
export default personalSubscriptionSlice.reducer;
